/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-nested-ternary */
import { CustomPagination } from 'components/CustomPagination';
import { SideDetailsLayout } from 'components/Layout/SideDetailsLayout';
import { SearchStringFilter } from 'components/SearchStringFilter';
import { TableLoader } from 'components/TableLoader';
import { getColumnId, TableSettings } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import { TotalCount } from 'components/TotalCount';
import { useRequest } from 'features/request/useRequest';
import type {
    ChapterEnum,
    WorkOrderPriceStatusEnum,
    ContractStatusEnum,
    WorkOrderVersionPriceData,
    WorkOrderVersionActKc2VersionPriceData,
    WorkOrderVersionSeasonPriceData,
} from 'gql/graphql';
import { ActKc2StatusEnum, CostGroupEnum, DeviationEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useSelection } from 'hooks/useSelection';
import { useTableCollapse } from 'hooks/useTableCollapse';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { CodeCell } from 'pages/Project/Common/CodeCell';
import type { WorkOrderPriceListItem } from 'pages/Project/UerProject/WorkOrder';
import { usePriceGetAdditionalClassName } from 'pages/Project/UerProject/WorkOrder/hooks/usePriceGetAdditionalClassName';
import { getTagLabel } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { rowsWordForms } from 'pages/Project/UerProject/WorkOrder/WorkOrderPrice';
import { TitleActKC2Columns } from 'pages/Project/UerProject/WorkOrder/WorkOrderPrice/TitleActKC2Columns';
import { WorkOrderPriceCodeCell } from 'pages/Project/UerProject/WorkOrder/WorkOrderPrice/WorkOrderPriceCodeCell';
import type { Row } from 'pages/Project/UerProject/WorkOrder/workorderTypes';
import type { TableColumn } from 'uikit';
import { Button, Grid, GridItem, Table, TableTextFilter } from 'uikit';
import { formatNumberToRu, formatNumberToRuF, getCheckedIdsArr } from 'utils';
import { ColoredColumnTitle, DeviatedCell, DeviatedColumnTitle } from 'utils/tableCells';

import * as R from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getPlanVolumeTermsRepresentation } from '../../WorkOrder/WorkOrderPrice/hooks';
import {
    getSeasonCompletedCostKey,
    getSeasonCompletedVolumeKey,
    getSeasonCostKey,
    getSeasonPlanVolumeTermsKey,
    getSeasonReminderCostKey,
    getSeasonReminderVolumeKey,
    getSeasonVolumeKey,
    showNotNull,
    useWorkOrderVersionSeasonsColumns,
} from '../hooks/useWorkOrderVersionSeasonsColumns';
import { useWorkOrderVersionTypeWorkColumns } from '../hooks/useWorkOrderVersionTypeWorkColumns';
import { gqlWorkOrderVersionActsKC2Version, gqlWorkOrderVersionPriceList } from '../workOrderVersionQueries';
import { WorkOrderVersionSummary } from '../WorkOrderVersionSummary';

const flatSeasons = (costRounding: number) => (item: WorkOrderVersionPriceData) => ({
    ...item,
    ...item.seasonPrices?.reduce((memo: any, value: WorkOrderVersionSeasonPriceData) => {
        const {
            seasonId,
            planVolume,
            planVolumeTerms,
            planCost,
            completedVolume,
            completedCost,
            remainderVolume,
            remainderCost,
        } = value;

        memo[getSeasonVolumeKey(seasonId)] = formatNumberToRu(planVolume, 3);
        memo[getSeasonPlanVolumeTermsKey(seasonId)] = getPlanVolumeTermsRepresentation(planVolumeTerms);
        memo[getSeasonCostKey(seasonId)] = formatNumberToRu(planCost, costRounding);
        memo[getSeasonCompletedVolumeKey(seasonId)] = formatNumberToRu(completedVolume, 3);
        memo[getSeasonCompletedCostKey(seasonId)] = formatNumberToRu(completedCost, costRounding);
        memo[getSeasonReminderVolumeKey(seasonId)] = formatNumberToRu(remainderVolume, 3);
        memo[getSeasonReminderCostKey(seasonId)] = formatNumberToRu(remainderCost, costRounding);

        return memo;
    }, {}),
});

const flatActs = (item: WorkOrderVersionPriceData) => ({
    ...item,
    ...item.actKC2VersionPrices?.reduce((memo: any, value: WorkOrderVersionActKc2VersionPriceData) => {
        const {
            actKC2VersionId,
            planVolume,
            planCost,
            acceptedVolume,
            acceptedCost,
            acceptedVolumeDeviation,
            planVolumeDeviation,
        } = value;

        memo[`actKC2planVolume_${actKC2VersionId}`] = formatNumberToRu(planVolume, 3);
        memo[`actKC2planCost_${actKC2VersionId}`] = formatNumberToRu(planCost, 2);
        memo[`actKC2acceptedVolume_${actKC2VersionId}`] = formatNumberToRu(acceptedVolume, 3);
        memo[`actKC2acceptedCost_${actKC2VersionId}`] = formatNumberToRu(acceptedCost, 2);
        memo[`actKC2acceptedVolumeDeviation_${actKC2VersionId}`] = acceptedVolumeDeviation;
        memo[`actKC2planVolumeDeviation_${actKC2VersionId}`] = planVolumeDeviation;

        return memo;
    }, {}),
});
const workOrderItemFormatter = (item: WorkOrderVersionPriceData, costRounding: number) => {
    const resultFormatted = R.pipe(
        flatSeasons(costRounding),
        flatActs,
        R.evolve({
            totalPlanCost: formatNumberToRuF(costRounding),
            totalPlanVolume: formatNumberToRuF(3),
            unitCost: formatNumberToRuF(2),
            totalCompletedCost: formatNumberToRuF(costRounding),
            totalCompletedVolume: formatNumberToRuF(3),
            totalRemainderCost: formatNumberToRuF(costRounding),
            totalRemainderVolume: formatNumberToRuF(3),
        }),
    );

    return resultFormatted(item);
};

const matchFilters = (item: any, tableFilters: any) =>
    Object.entries<string>(tableFilters).reduce((acc, [key, filterValue]) => {
        const itemValue = item?.[key]?.toLocaleLowerCase();

        const filterValueLowered = R.is(String, filterValue) ? filterValue?.toLocaleLowerCase() : filterValue;

        acc = acc && itemValue.includes(filterValueLowered);

        return acc;
    }, true);

interface IWorkOrderPriceProps {
    workOrderId: number;
    constructionTypeId: number;
    chapter: ChapterEnum;
    workOrderObjectId: number;
    workOrderSubObjectId: number;
    onRefresh: () => void;
    contractStatus?: ContractStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    isAllAktKC2Accepted?: boolean;
    reload?: any;
    isDisabledByProjectStatus: boolean;
    costRounding: number;
}

interface IWorkOrderPriceKC2Volume {
    key: string;
    deviationKey: string;
}

const getRenderWorkOrderPriceKC2Volume =
    ({ key, deviationKey }: IWorkOrderPriceKC2Volume) =>
    // eslint-disable-next-line react/display-name
    (row: any) =>
        row[deviationKey] === DeviationEnum.Up ? <DeviatedCell>{row[key]}</DeviatedCell> : row[key];

export const WorkOrderVersionPrice = ({
    reload,
    constructionTypeId,
    workOrderId,
    chapter,
    workOrderObjectId,
    workOrderSubObjectId,
    workOrderStatus,
    contractStatus,
    onRefresh,
    isDisabledByProjectStatus,
    costRounding,
}: IWorkOrderPriceProps) => {
    const dispatch = useDispatch();

    const [tableFilters, setTableFilters] = useState<{ code?: string; name?: string }>({});
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [currentPage, setCurrentPage] = useState<any>(0);
    const [perPage, setPerPage] = useState<number>(100);

    const variables = useMemo(
        () => ({
            workOrderId,
            chapter,
            workOrderObjectId: workOrderSubObjectId,
        }),
        // eslint-disable-next-line
        [reload, constructionTypeId, workOrderId, chapter, workOrderObjectId, workOrderSubObjectId],
    );

    const actsVariables = useMemo(
        () => ({
            costGroup: CostGroupEnum.Cmr,
            workOrderVersionId: workOrderId,
            workOrderVersionObjectId: workOrderObjectId,
        }),
        // eslint-disable-next-line
        [workOrderId, workOrderObjectId, reload],
    );

    useEffect(() => {
        setCurrentPage(0);
    }, [variables, tableFilters]);

    useEffect(() => {
        setCheckedIds({});
    }, [variables]);

    const [workOrderPriceListData, workOrderPriceListStatus, , workOrderPriceListAC] = useRequest({
        requestId: 'workOrderPriceList',
        query: gqlWorkOrderVersionPriceList,
        variables,
        withEffect: Boolean(chapter && workOrderSubObjectId),
    });

    const [actsKC2VersionResultData] = useRequest({
        requestId: 'workOrderVersionOthersActs',
        query: gqlWorkOrderVersionActsKC2Version,
        variables: actsVariables,
        withEffect: Boolean(workOrderId),
    });

    const actsKC2 = useMemo(
        () => actsKC2VersionResultData?.uerWorkOrderVersionActsKC2Version ?? [],
        [actsKC2VersionResultData?.uerWorkOrderVersionActsKC2Version],
    );

    const priceList = workOrderPriceListData?.uerWorkOrderVersionPriceList;

    const [isSummaryDetailsVisible, setIsSummaryDetailsVisible] = useState(false);

    const onSummaryDetailsVisibilityChange = useCallback((isVisible) => {
        setIsSummaryDetailsVisible(isVisible);
    }, []);

    const items = useMemo(() => priceList?.items ?? [], [priceList]);

    const handleOnRowChange = useCallback(() => {
        dispatch(workOrderPriceListAC(variables));
    }, [dispatch, variables, workOrderPriceListAC]);

    const actKC2Columns = useMemo(() => {
        let isOddSeason = true;
        const currentSeasonId = items?.[0]?.seasonPrices?.[0]?.seasonId;

        return actsKC2.map((item, index: number) => {
            const { id, seasonId, status, number, date, isCorrection, correctionDate, isInactive, fullNumber } = item;

            if (seasonId !== currentSeasonId) {
                isOddSeason = !isOddSeason;
            }

            const volumeColAccepted = [
                {
                    title: (
                        <ColoredColumnTitle isOdd={isOddSeason} align="left">
                            <DeviatedColumnTitle
                                title="Объем (План)"
                                deviation={DeviationEnum.None}
                                tooltipText="Превышает остаток по сезону"
                            />
                        </ColoredColumnTitle>
                    ),
                    accessor: `actKC2planVolume_${id}`,
                    align: 'right',
                    width: 100,
                    renderCell: getRenderWorkOrderPriceKC2Volume({
                        key: `actKC2planVolume_${id}`,
                        deviationKey: `actKC2planVolumeDeviation_${id}`,
                    }),
                },
            ];

            const planColAccepted = [
                {
                    title: (
                        <ColoredColumnTitle isOdd={isOddSeason} align="right">
                            Стоимость (План)
                        </ColoredColumnTitle>
                    ),
                    accessor: `actKC2planCost_${id}`,
                    className: `actKC2planCost_${index}`,
                    align: 'right',
                    width: 112,
                    renderCell: (row: any) => (status !== ActKc2StatusEnum.Accepted ? row[`actKC2planCost_${id}`] : ''),
                },
            ];

            return {
                seasonId,
                id,
                title: (
                    <ColoredColumnTitle isOdd={isOddSeason} align="left">
                        <TitleActKC2Columns
                            id={id}
                            number={number}
                            fullNumber={fullNumber}
                            status={status}
                            date={date}
                            correctionDate={correctionDate}
                            onRefresh={onRefresh}
                            isCorrection={isCorrection}
                            contractStatus={contractStatus}
                            workOrderStatus={workOrderStatus}
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            canCreateCorrection
                        />
                    </ColoredColumnTitle>
                ),

                withoutPadding: true,
                accessor: formatActTitle(number, date),
                align: 'left',
                columns: [
                    ...(status !== ActKc2StatusEnum.Accepted && `actKC2planVolume_${id}` ? volumeColAccepted : []),
                    ...(status !== ActKc2StatusEnum.Accepted && `actKC2planCost_${id}` ? planColAccepted : []),
                    {
                        title: (
                            <ColoredColumnTitle isOdd={isOddSeason} align="left">
                                <DeviatedColumnTitle
                                    title="Объем (Согласовано)"
                                    deviation={DeviationEnum.None}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            </ColoredColumnTitle>
                        ),
                        accessor: `actKC2acceptedVolume_${id}`,
                        width: 135,
                        align: 'right',
                        renderCell: (row: Row) => (
                            <div className={isInactive ? 'inactive-act' : ''} style={{ width: '100%' }}>
                                {getRenderWorkOrderPriceKC2Volume({
                                    key: `actKC2acceptedVolume_${id}`,
                                    deviationKey: `actKC2acceptedVolumeDeviation_${id}`,
                                })(row)}
                            </div>
                        ),
                    },
                    {
                        title: (
                            <ColoredColumnTitle isOdd={isOddSeason} align="right">
                                Стоимость (Согласовано)
                            </ColoredColumnTitle>
                        ),
                        accessor: `actKC2acceptedCost_${id}`,
                        className: `actKC2acceptedCost_${index}`,
                        align: 'right',
                        width: 112,
                        renderCell: (row: any) => {
                            const actKC2IdData = `actKC2acceptedCost_${id}`;

                            return <div className={isInactive ? 'inactive-act' : ''}>{row[actKC2IdData]}</div>;
                        },
                    },
                ],
            };
        });
    }, [items, actsKC2, onRefresh, contractStatus, workOrderStatus, isDisabledByProjectStatus]);

    const seasonAndLinkedActsColumns = useWorkOrderVersionSeasonsColumns({
        workOrderStatus,
        workOrderId,
        onChange: handleOnRowChange,
        actsColumns: actKC2Columns,
        contractStatus,
    });

    const typeWorkColumns = useWorkOrderVersionTypeWorkColumns(workOrderStatus);

    const [selection, , , setSelection] = useSelection('WorkOrderPriceVersion');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const baseColumns = useMemo(
        () => [
            {
                title: 'Статус',
                accessor: 'status',
                align: 'center',
                width: 100,
                renderCell: (row: WorkOrderVersionPriceData) => row.status && <CodeCell status={row.status} />,
            },
            {
                title: 'Шифр',
                accessor: 'code',
                width: 160,
                align: 'center',
                renderCell: (row: Row) => (
                    // @ts-ignore
                    <WorkOrderPriceCodeCell row={row as WorkOrderPriceListItem} workOrderStatus={workOrderStatus} />
                ),
            },
            {
                title: 'Наименование',
                accessor: 'name',
                width: 400,
            },
            {
                title: 'Ед. изм.',
                accessor: 'unit',
                align: 'center',
                width: 80,
            },
            {
                hiddenTitle: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                accessor: 'unitCost',
                align: 'right',
                width: 172,
                // eslint-disable-next-line react/display-name
                control: () => (
                    <TitleWithTooltipColumns
                        title="Стоимость единицы в текущем уровне цен, руб. без НДС"
                        childTooltip="Стоимость единицы в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
                    />
                ),
            },
            {
                title: 'План по НЗ',
                accessor: 'plan',
                columns: [
                    {
                        title: 'Объем всего',
                        accessor: 'totalPlanVolume',
                        align: 'right',
                        width: 112,
                        renderCell: (row: Row) => showNotNull(row?.totalPlanVolume),
                    },
                    {
                        accessor: 'totalPlanCost',
                        align: 'right',
                        width: 124,
                        // eslint-disable-next-line react/display-name
                        control: () => (
                            <TitleWithTooltipColumns
                                title="Стоимость всего, руб. без НДС"
                                childTooltip="Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
                            />
                        ),
                        renderCell: (row: Row) => showNotNull(row?.totalPlanCost),
                    },
                ],
            },
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          title: 'Выполнено по нз',
                          accessor: 'completed',
                          columns: [
                              {
                                  title: 'Объем всего',
                                  accessor: 'totalCompletedVolume',
                                  align: 'right',
                                  width: 112,
                                  renderCell: (row: Row) => showNotNull(row?.totalCompletedVolume),
                              },
                              {
                                  // eslint-disable-next-line react/display-name
                                  control: () => (
                                      <TitleWithTooltipColumns
                                          title="Стоимость всего, руб. без НДС"
                                          childTooltip="Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
                                      />
                                  ),
                                  accessor: 'totalCompletedCost',
                                  align: 'right',
                                  width: 124,
                                  renderCell: (row: Row) => showNotNull(row?.totalCompletedCost),
                              },
                          ],
                      },
                      {
                          title: 'Остаток по нз',
                          accessor: 'remainder',
                          columns: [
                              {
                                  title: 'Объем всего',
                                  accessor: 'totalRemainderVolume',
                                  align: 'right',
                                  width: 112,
                                  renderCell: (row: Row) => showNotNull(row?.totalRemainderVolume),
                              },
                              {
                                  // eslint-disable-next-line react/display-name
                                  control: () => (
                                      <TitleWithTooltipColumns
                                          title="Стоимость всего, руб. без НДС"
                                          childTooltip="Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
                                      />
                                  ),
                                  accessor: 'totalRemainderCost',
                                  align: 'right',
                                  width: 124,
                                  renderCell: (row: Row) => showNotNull(row?.totalRemainderCost),
                              },
                          ],
                      },
                  ]
                : []),
        ],
        [workOrderStatus],
    ) as TableColumn<any>[];

    const filtersConfig = useMemo(
        () => [
            { id: 'code', name: 'Шифр', field: 'code', filterer: R.T, component: { name: SearchStringFilter } },
            {
                id: 'name',
                name: 'Наименование',
                field: 'name',
                filterer: R.T,
                component: { name: SearchStringFilter },
            },
            {
                id: 'status',
                name: 'Статус',
                field: 'status',
                filterer: R.T,
                component: {
                    name: TableTextFilter,
                    props: {
                        items: [
                            { name: 'Черновик', value: 'DRAFT' },
                            { name: 'Новая запись', value: 'NEW_RECORD' },
                        ],
                    },
                },
            },
        ],
        [],
    );

    const getAdditionalClassName = usePriceGetAdditionalClassName(priceList, chapter, workOrderStatus);

    const groupedData = useMemo(() => {
        const formattedItems = items.map((item: any) => workOrderItemFormatter(item, costRounding));
        const childrenItems = formattedItems.filter(
            (item: any) => item.parentId !== null && matchFilters(item, tableFilters),
        );
        const matchedParentIds = childrenItems.map((item: any) => item.parentId);
        const rootItems = formattedItems.filter(
            (item: any) =>
                item.parentId === null && (matchFilters(item, tableFilters) || matchedParentIds.includes(item.id)),
        );

        return rootItems.map((rootItem: any) => ({
            ...rootItem,
            rows: childrenItems.filter((item: any) => item.parentId === rootItem.id),
        }));
    }, [costRounding, items, tableFilters]);

    const currentPageGroupedData = useMemo(
        () => groupedData.slice(currentPage * perPage, (currentPage + 1) * perPage),
        [currentPage, groupedData, perPage],
    );

    const totalPages = Math.ceil(groupedData.length / perPage);

    const [columnsWithCollapse, rows] = useTableCollapse('orderTable', baseColumns, currentPageGroupedData);

    const optionalColumns = useMemo(
        () => [...columnsWithCollapse, ...typeWorkColumns, ...seasonAndLinkedActsColumns],
        [columnsWithCollapse, seasonAndLinkedActsColumns, typeWorkColumns],
    ) as TableColumn<any>[];

    const columns = useMemo(
        () => optionalColumns.filter((column) => isColumnVisible(getColumnId(column))),
        [isColumnVisible, optionalColumns],
    ) as TableColumn<any>[];

    const onTableSettingsConfirm = useCallback(
        (visible) => {
            const hidden = optionalColumns.reduce(
                (acc, column) => {
                    acc[getColumnId(column)] = false;

                    return acc;
                },
                {} as Record<string, false>,
            );

            setSelection({ ...hidden, ...visible });
        },
        [optionalColumns, setSelection],
    );

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const handleTableFilterUpdated = useCallback((selectedFilters: any) => {
        setTableFilters((filtersState: any) =>
            R.pickBy(R.is(String), {
                ...filtersState,
                code: selectedFilters.code?.value,
                name: selectedFilters.name?.value,
                status:
                    selectedFilters.status?.value?.length === 1
                        ? selectedFilters.status?.value?.map(
                              (val: { name: string; value: WorkOrderPriceStatusEnum }) => val.value,
                          )[0]
                        : [],
            }),
        );
    }, []);

    return (
        <>
            <Grid cols={2} className="TableSettings">
                <GridItem>
                    {checkedIdsArr.length ? (
                        <Grid cols={4} yAlign="center">
                            <GridItem>
                                <TotalCount
                                    totalCount={checkedIdsArr.length}
                                    forms={rowsWordForms}
                                    prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                />
                            </GridItem>
                            <GridItem col={3}>
                                <Button label="Сбросить" view="ghost" size="xs" onClick={handleClearChecked} />
                            </GridItem>
                        </Grid>
                    ) : (
                        <TotalCount totalCount={rows.length} />
                    )}
                </GridItem>
                <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                    <TableSettings
                        columns={optionalColumns}
                        onConfirm={onTableSettingsConfirm}
                        isColumnVisible={isColumnVisible}
                    />
                </GridItem>
            </Grid>
            <SideDetailsLayout
                onDetailsVisibilityChange={onSummaryDetailsVisibilityChange}
                collapsedTitle="Показать итоги"
                expandedTitle="Скрыть итоги"
            >
                <Table
                    columns={columns}
                    rows={rows}
                    filters={filtersConfig}
                    onFiltersUpdated={handleTableFilterUpdated}
                    getAdditionalClassName={getAdditionalClassName}
                    getTagLabel={getTagLabel}
                    size="s"
                    className="workOrderPrice"
                    stickyHeader
                    emptyRowsPlaceholder={<TableLoader requestStatus={workOrderPriceListStatus} />}
                    borderBetweenColumns
                    verticalAlign="center"
                />
                <WorkOrderVersionSummary
                    chapter={chapter}
                    workOrderObjectId={workOrderObjectId}
                    isVisible={isSummaryDetailsVisible}
                    actsKc2={actsKC2}
                />
            </SideDetailsLayout>
            <CustomPagination
                position="space-between"
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                currentPageSize={perPage}
                setCurrentPageSize={setPerPage}
            />
        </>
    );
};

WorkOrderVersionPrice.displayName = 'WorkOrderVersionPrice';
